import type { LucideIcon } from "lucide-react";
import { Activity, BookOpen, Building2, Cable, Cpu, CreditCard, FileClock, Fingerprint, Gauge, KeyRound, LayoutDashboard, LifeBuoy, ListTree, LogOut, Menu, PackageCheck, ReceiptText, Settings2, ShieldCheck, Siren, Users, X } from "lucide-react";
import type { PlatformAction, SessionView } from "@control-plane/contracts";
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { AssistantPanel } from "./AssistantPanel";
import { api } from "../lib/api";

type NavEntry = { to: string; label: string; icon: LucideIcon; action?: PlatformAction; roles?: readonly SessionView["admin"]["role"][]; end?: boolean };
type NavWorkspace = { key: string; label: string; icon: LucideIcon; entries: readonly NavEntry[] };
type SupportIdentity = {
  status: "active" | "expired" | "ended";
  statusLabel: string;
  tenantLabel: string;
  operatorLabel: string;
  permissionSummary: string;
  remainingText: string;
};
type SupportIdentityState = { loading: boolean; item: SupportIdentity | null; error: string };

/**
 * P1 信息架构：16 个历史一级入口收敛为 5 个工作区。
 * 旧 URL 全部保留兼容（App.tsx 路由不变）；权限隐藏只改善体验，不是安全边界，
 * 后端仍按 action 校验。禁止在此之外新增平行导航。
 */
export const workspaces: readonly NavWorkspace[] = [
  {
    key: "today", label: "今日总管", icon: LayoutDashboard,
    entries: [{ to: "/", label: "今日总管", icon: LayoutDashboard, end: true, action: "dashboard.read" }],
  },
  {
    key: "commerce", label: "客户与商业", icon: Building2,
    entries: [
      { to: "/tenants", label: "租户与申请", icon: Building2, action: "tenant.read" },
      { to: "/plans", label: "套餐目录", icon: PackageCheck, action: "plans.read" },
      { to: "/subscriptions", label: "订阅中心", icon: ReceiptText, action: "subscriptions.read" },
      { to: "/orders-billing", label: "订购与账单", icon: CreditCard, action: "orders.read" },
      { to: "/usage", label: "用量与额度", icon: Gauge, action: "usage.read" },
      { to: "/runtime", label: "服务账号与运行策略", icon: Cpu, action: "usage.read" },
    ],
  },
  {
    key: "platform", label: "平台运行", icon: Cpu,
    entries: [
      { to: "/integrations", label: "平台连接与投递", icon: Cable, action: "delivery.manage" },
      { to: "/service-config", label: "服务配置中心", icon: Settings2, action: "service_config.read" },
      { to: "/category-attributes", label: "类目属性库", icon: ListTree, action: "catalog.category_attributes.read" },
      { to: "/operations", label: "事故与维护", icon: Siren, action: "health.read" },
      { to: "/health", label: "平台健康", icon: Activity, action: "health.read" },
    ],
  },
  {
    key: "security", label: "安全与组织", icon: ShieldCheck,
    entries: [
      { to: "/enterprise", label: "企业治理与审批", icon: ShieldCheck, action: "enterprise.manage" },
      { to: "/credentials", label: "凭据保险库", icon: KeyRound, action: "credential.read_metadata", roles: ["platform_owner", "security_admin"] },
      { to: "/admins", label: "平台管理员", icon: Users, action: "admin.read" },
      { to: "/support", label: "受控代管", icon: Fingerprint, action: "tenant.read" },
      { to: "/audit", label: "审计链", icon: FileClock, action: "audit.read" },
    ],
  },
  {
    key: "help", label: "帮助与设置", icon: LifeBuoy,
    entries: [
      { to: "/security", label: "账号安全", icon: ShieldCheck },
      { to: "/manual", label: "使用说明", icon: BookOpen },
    ],
  },
] as const;

export function visibleWorkspaces(actions: readonly string[], role?: SessionView["admin"]["role"]): Array<NavWorkspace & { entries: NavEntry[] }> {
  return workspaces
    .map((workspace) => ({
      ...workspace,
      entries: workspace.entries.filter((entry) =>
        (!entry.action || actions.includes(entry.action)) &&
        (!entry.roles || (role ? entry.roles.includes(role) : false))
      ),
    }))
    .filter((workspace) => workspace.entries.length > 0);
}

function recordOf(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function arrayOf(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function textOf(value: unknown, fallback: string) {
  return typeof value === "string" && value.trim()
    ? value.trim().replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, "所选对象").slice(0, 160)
    : fallback;
}

function supportRemainingText(value: unknown, expiresAt: unknown) {
  const seconds = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(seconds) && seconds > 0) return `剩余时间 ${Math.ceil(seconds / 60)} 分钟`;
  const dateText = typeof expiresAt === "string" && expiresAt.trim() ? new Date(expiresAt) : null;
  if (dateText && !Number.isNaN(dateText.getTime())) return `已到期：${dateText.toLocaleString("zh-CN")}`;
  return "剩余时间待平台确认";
}

function normalizeSupportIdentity(value: unknown): SupportIdentity | null {
  const rows = arrayOf(recordOf(value).items).map((item) => {
    const record = recordOf(item);
    const status = textOf(record.status, "");
    if (!["active", "expired", "revoked", "ended"].includes(status)) return null;
    return {
      status: status === "active" ? "active" as const : status === "expired" ? "expired" as const : "ended" as const,
      statusLabel: textOf(record.statusLabel, status === "active" ? "进行中" : status === "expired" ? "已到期" : "已结束"),
      tenantLabel: textOf(record.tenantLabel, "企业待确认"),
      operatorLabel: textOf(record.operatorLabel, "操作员待确认"),
      permissionSummary: textOf(record.permissionSummary, "权限范围待确认"),
      remainingText: supportRemainingText(record.remainingSeconds ?? record.remainingMinutes, record.expiresAt),
    };
  }).filter((item): item is SupportIdentity => Boolean(item));
  return rows.find((item) => item.status === "active") ?? rows[0] ?? null;
}

export function AppShell({ session, onLogout, currentPath, onNavigate, children }: { session: SessionView; onLogout: () => void; currentPath: string; onNavigate: (path: string) => void; children: ReactNode }) {
  // 移动端抽屉导航：菜单按钮 ≥44×44，抽屉内全部展示中文标签（UI-007）。
  const [navOpen, setNavOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const [supportIdentity, setSupportIdentity] = useState<SupportIdentityState>({ loading: false, item: null, error: "" });
  function navigate(event: MouseEvent<HTMLAnchorElement>, path: string) { event.preventDefault(); onNavigate(path); setNavOpen(false); }
  function isActive(entry: NavEntry) { return entry.end ? currentPath === entry.to : currentPath === entry.to || currentPath.startsWith(`${entry.to}/`); }
  // 抽屉打开时聚焦抽屉、锁定背景滚动；Escape 或关闭后把焦点还给菜单按钮（焦点恢复）。
  useEffect(() => {
    if (!navOpen) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    drawerRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) { if (event.key === "Escape") setNavOpen(false); }
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = originalOverflow;
      (previous ?? menuButtonRef.current)?.focus();
    };
  }, [navOpen]);
  useEffect(() => {
    if (!session.actions.includes("tenant.read")) {
      setSupportIdentity({ loading: false, item: null, error: "" });
      return;
    }
    let cancelled = false;
    async function loadSupportIdentity() {
      setSupportIdentity((current) => ({ ...current, loading: true, error: "" }));
      try {
        const data = await api<unknown>("/api/support-grants");
        if (!cancelled) setSupportIdentity({ loading: false, item: normalizeSupportIdentity(data), error: "" });
      } catch (failure) {
        if (!cancelled) setSupportIdentity({ loading: false, item: null, error: failure instanceof Error ? failure.message : "代管状态读取失败" });
      }
    }
    void loadSupportIdentity();
    const listener = () => { void loadSupportIdentity(); };
    window.addEventListener("support-identity-refresh", listener);
    return () => {
      cancelled = true;
      window.removeEventListener("support-identity-refresh", listener);
    };
  }, [currentPath, session.actions]);
  const navWorkspaces = visibleWorkspaces(session.actions, session.admin.role);
  return <div className="app-shell">
    <header className="mobile-topbar">
      <button ref={menuButtonRef} type="button" className="mobile-menu-button" aria-expanded={navOpen} aria-controls="mobile-nav-drawer" onClick={() => setNavOpen(true)}><Menu size={18} aria-hidden /><span>菜单</span></button>
      <div className="mobile-brand"><ShieldCheck size={16} aria-hidden /><span><strong>亿开电商</strong><small>平台总管</small></span></div>
    </header>
    {navOpen && <>
      <div className="mobile-nav-backdrop" onClick={() => setNavOpen(false)} />
      <div id="mobile-nav-drawer" ref={drawerRef} className="mobile-nav" role="dialog" aria-modal="true" aria-label="主导航" tabIndex={-1}>
        <div className="mobile-nav-head"><strong>亿开电商 · 平台总管</strong><button type="button" className="mobile-menu-button" onClick={() => setNavOpen(false)}><X size={18} aria-hidden /><span>关闭</span></button></div>
        <nav aria-label="主导航">{navWorkspaces.map((workspace) => <div key={workspace.key} className="nav-workspace">
          <p className="nav-workspace-label"><workspace.icon size={14} aria-hidden /><span>{workspace.label}</span></p>
          {workspace.entries.map((entry) => <a key={entry.to} href={entry.to} className={isActive(entry) ? "active" : ""} aria-current={isActive(entry) ? "page" : undefined} onClick={(event) => navigate(event, entry.to)}><entry.icon size={17} aria-hidden /><span>{entry.label}</span></a>)}
        </div>)}</nav>
        <div className="mobile-nav-footer"><div className="avatar">{session.admin.displayName.slice(0, 1).toUpperCase()}</div><div><strong>{session.admin.displayName}</strong><small>{session.admin.role}</small></div><button className="icon-button" onClick={onLogout} aria-label="退出登录"><LogOut size={17} /></button></div>
      </div>
    </>}
    <aside className="sidebar">
      <div className="brand"><span><ShieldCheck /></span><div><strong>亿开电商</strong><small>平台总管 · CONTROL PLANE</small></div></div>
      <nav aria-label="主导航">{navWorkspaces.map((workspace) => <div key={workspace.key} className="nav-workspace">
        <p className="nav-workspace-label" aria-label={workspace.label} title={workspace.label}><workspace.icon size={14} aria-hidden /><span>{workspace.label}</span></p>
        {workspace.entries.map((entry) => <a key={entry.to} href={entry.to} className={isActive(entry) ? "active" : ""} aria-current={isActive(entry) ? "page" : undefined} onClick={(event) => navigate(event, entry.to)}><entry.icon size={17} aria-hidden /><span>{entry.label}</span></a>)}
      </div>)}</nav>
      <div className="sidebar-footer"><div className="avatar">{session.admin.displayName.slice(0, 1).toUpperCase()}</div><div><strong>{session.admin.displayName}</strong><small>{session.admin.role}</small></div><button className="icon-button" onClick={onLogout} aria-label="退出登录"><LogOut size={17} /></button></div>
    </aside>
    <main className="content">
      <SupportIdentityBar state={supportIdentity} onOpenSupport={() => onNavigate("/support")} />
      {children}
      <AssistantPanel session={session} currentPath={currentPath} />
    </main>
  </div>;
}

function SupportIdentityBar({ state, onOpenSupport }: { state: SupportIdentityState; onOpenSupport: () => void }) {
  if (!state.item && !state.error) return null;
  if (state.error) return <div className="support-identity-bar blocked" role="status">
    <Fingerprint size={16} aria-hidden />
    <span>代管状态暂不可用：{state.error}</span>
    <button type="button" className="button ghost" onClick={onOpenSupport}>查看代管</button>
  </div>;
  const item = state.item;
  if (!item) return null;
  return <div className={`support-identity-bar ${item.status}`} role="status">
    <Fingerprint size={16} aria-hidden />
    <span><strong>{item.operatorLabel}</strong> 正在代管 <strong>{item.tenantLabel}</strong></span>
    <span>{item.permissionSummary}</span>
    <span className="app-number">{item.status === "active" ? item.remainingText : item.statusLabel}</span>
    <button type="button" className="button ghost" onClick={onOpenSupport}>{item.status === "active" ? "查看 / 结束代管" : "查看代管状态"}</button>
  </div>;
}
